import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';
import { axiosInstance } from '../routes/axiosInstance';
import { utcDayStart } from '../components/insights/prepare';

// The API serves at most 31 days (MAX_RANGE_DAYS); longer windows get none.
export const INSIGHTS_MAX_DAYS = 31;
export const insightsTooLong = ({ startDate, endDate }) =>
  moment.utc(endDate).diff(moment.utc(startDate), 'days') + 1 > INSIGHTS_MAX_DAYS;

// Arguments for fetchInsights, or null when nothing should be requested.
export function insightsRequest({ on, resourceId, startDate, endDate, model, horizonFilter, rampMethod, observed }) {
  if (!on || !resourceId || !startDate || !endDate) return null;
  if (insightsTooLong({ startDate, endDate })) return null;
  return { resourceId, startDate, endDate, model, horizonFilter, rampMethod, observed };
}

// Key of a load: the applied fetch signature base plus the ramp method and
// the observed-ramps layer, so a switch refetches; null before any filter is applied.
const OBSERVED_KEY = '|observed';
export const insightsKey = (base, rampMethod, observed = false) => (
  base == null ? null : `${base}|ramps=${rampMethod}${observed ? OBSERVED_KEY : ''}`);
// The applied filters of a key, without the ramp method and the layer.
const keyBase = (key) => {
  const at = key == null ? -1 : key.lastIndexOf('|ramps=');
  return at < 0 ? key : key.slice(0, at);
};
export const sameInsightsBase = (a, b) => a != null && keyBase(a) === keyBase(b);
// The data loaded for `loaded` serve `wanted`: the same key, or the same one
// with observed ramps loaded that are now only hidden.
export const insightsKeyServes = (loaded, wanted) => loaded != null
  && (loaded === wanted || loaded === `${wanted}${OBSERVED_KEY}`);

// Ensemble insights for the dashboard chart (GET /market/insights, spec 6.3);
// `rampMethod` picks which ramp pointers the API serves; `observed` adds the
// ramps measured in the output (addendum spec 7).
export const fetchInsights = createAsyncThunk(
  'insights/fetchInsights',
  async ({ resourceId, startDate, endDate, model, horizonFilter = 'latest', rampMethod, observed }, { rejectWithValue }) => {
    try {
      const query = new URLSearchParams({
        resource: resourceId,
        start_date: `${moment.utc(startDate).format('YYYY-MM-DD')}T00:00:00Z`,
        end_date: `${moment.utc(endDate).format('YYYY-MM-DD')}T23:59:59Z`,
        model,
        horizon: horizonFilter,
        ramps: rampMethod || 'forecast',
        ...(observed ? { observed: '1' } : {}),
      });
      const response = await axiosInstance.get(`/market/insights/?${query.toString()}`);
      // One "past" cut-off per load, shared by pins, plugin labels and counts.
      return { data: response.data.data, pastCutoff: utcDayStart(Date.now() / 1000) };
    } catch (error) {
      console.error('Failed to fetch insights:', error);
      return rejectWithValue('Failed to fetch insights.');
    }
  },
);

const insightsSlice = createSlice({
  name: 'insights',
  // requestId: the latest fetch; older responses, and any after a clear, are
  // dropped so they cannot overwrite newer data. status: why nothing shows
  // ('too_long' from clearInsights, 'failed' from a rejected fetch) or null.
  // loadedKey: the applied fetch signature `data` was loaded for (arg `key`).
  // aboutTab: the About tab to open on ('indicators' or 'ramp'); aboutTabSeq
  // counts the asks, so asking again for the same tab switches an open panel.
  initialState: {
    data: null, loading: false, error: '', aboutOpen: false, aboutTab: 'indicators', aboutTabSeq: 0, requestId: null,
    pastCutoff: null, status: null, loadedKey: null,
  },
  reducers: {
    clearInsights(state, action) {
      state.data = null;
      state.error = '';
      state.loading = false;
      state.requestId = null;
      state.loadedKey = null;
      state.status = action.payload || null;
    },
    setAboutOpen(state, action) {
      state.aboutOpen = action.payload;
      if (action.payload) state.aboutTab = 'indicators';
    },
    // Opens the About panel on one tab (the Observed strip's link).
    openAbout(state, action) {
      state.aboutOpen = true;
      state.aboutTab = action.payload;
      state.aboutTabSeq += 1;
    },
    // The data shown already serve the filters again: a load still running
    // for another key must not replace them.
    dropPendingInsights(state) {
      state.requestId = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInsights.pending, (state, action) => {
        state.loading = true;
        state.error = '';
        state.status = null;
        state.requestId = action.meta.requestId;
      })
      .addCase(fetchInsights.fulfilled, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        state.loading = false;
        state.data = action.payload.data;
        state.pastCutoff = action.payload.pastCutoff;
        state.loadedKey = action.meta.arg.key ?? null;
      })
      .addCase(fetchInsights.rejected, (state, action) => {
        if (action.meta.requestId !== state.requestId) return;
        // Data kept during a ramp-method or layer switch would read as current.
        state.data = null;
        state.loadedKey = null;
        state.loading = false;
        state.status = 'failed';
        state.error = action.payload || 'Failed to fetch insights.';
      });
  },
});

export const { clearInsights, dropPendingInsights, openAbout, setAboutOpen } = insightsSlice.actions;
// Who may use insights: superusers and market owners (read-only included),
// never forecasters. The API limits market owners to the resources they manage.
export const selectCanUseInsights = (state) =>
  state.auth?.isAdmin === true || state.auth?.isMarketOwner === true || state.auth?.isMarketOwnerReadOnly === true;
export const selectInsightsData = (state) => state.insights.data;
export const selectInsightsPastCutoff = (state) => state.insights.pastCutoff;
export const selectInsightsStatus = (state) => state.insights.status;
export const selectInsightsLoadedKey = (state) => state.insights.loadedKey;
export const selectInsightsLoading = (state) => state.insights.loading;
export const selectInsightsAboutOpen = (state) => state.insights.aboutOpen;
export const selectInsightsAboutTab = (state) => state.insights.aboutTab;
export const selectInsightsAboutTabSeq = (state) => state.insights.aboutTabSeq;
export default insightsSlice.reducer;
